import React from 'react';
import { CurrencyDollar, Printer } from '@phosphor-icons/react';
import { buildTheoreticalPayout } from '../../utils/payoutSplits';
import { PAYOUT_PRESETS } from '../../utils/payoutPresets';

export const PayoutPanel = ({
  show, selectedClass, classEntries, payoutData, payoutLoading,
  placesPaid, divisionPlaces, divisionPoolPct,
  poolOverride, onSetPoolOverride,
  onSetPlacesPaid, onSetDivisionPlaces, onSetDivisionPoolPct,
  onCalculatePayouts, onSavePayoutConfig,
}) => {
  // v244 — Division time splits are now configured on the class itself
  // (see ShowEdit → Class Settings). We only *read* the resolved splits
  // here for the theoretical payout preview + preset auto-fill.
  const hasDivisions = selectedClass.division_type && !['NONE', 'OVERALL'].includes(selectedClass.division_type);
  const divNames = selectedClass.division_type === '4D' ? ['1D','2D','3D','4D'] :
    selectedClass.division_type === '3D' ? ['1D','2D','3D'] : ['1D','2D'];

  // v261 — Resolve the pool source class (if the producer linked this
  // class's payout pool to another class's entries in Class Settings).
  const poolSourceClass = React.useMemo(() => {
    const srcId = selectedClass?.entry_count_source_class_id;
    if (!srcId || !Array.isArray(show?.classes)) return null;
    return show.classes.find(c => c.id === srcId) || null;
  }, [selectedClass, show]);
  // Live count of paid entries in the source class — derived from the
  // /classes/{id}/entries payload if we have it, otherwise falls back to
  // whatever the last calculation recorded on payoutData.
  const poolSourceCount = payoutData?.pool_source_entry_count ?? null;

  // Association fee deduction logic (mirrors backend)
  const ASSOC_FEES = { IBRA: 2, NPBA: 2, NFRA: 2, NBHA: 3 };
  const className = (selectedClass.name || '').toUpperCase();
  const assocDeduction = Object.entries(ASSOC_FEES).reduce((sum, [key, fee]) => className.includes(key) ? sum + fee : sum, 0);
  const effectiveFee = Math.max((selectedClass.entry_fee || 0) - assocDeduction, 0);

  return (
    <div className="p-5" data-testid="payouts-panel">
      <div className="mb-4">
        <p className="text-sm text-[#9E9E9E] mb-3">
          {classEntries.length} entries &bull; ${selectedClass.entry_fee} fee &bull; {selectedClass.payback_percent}% payback
          {(selectedClass.added_money || 0) > 0 && ` + $${selectedClass.added_money} added`}
        </p>
        {assocDeduction > 0 && (
          <p className="text-xs text-[#FF9500] mb-2" data-testid="assoc-deduction-info">
            Association fees: -${assocDeduction}/entry
            ({Object.entries(ASSOC_FEES).filter(([k]) => className.includes(k)).map(([k, v]) => `${k} $${v}`).join(' + ')})
            &bull; Effective fee for payback: ${effectiveFee}
          </p>
        )}
        <p className="text-white text-sm font-semibold mb-4">
          Estimated Pool: <span className="text-[#34C759]">${(((poolSourceCount !== null && poolSourceClass) ? poolSourceCount : classEntries.length) * effectiveFee * (selectedClass.payback_percent || 70) / 100 + (selectedClass.added_money || 0)).toFixed(0)}</span>
        </p>
        {poolSourceClass && (
          <div className="mb-4 -mt-2 flex items-start gap-2 text-[11px] text-[#FFCC00] bg-[#FFCC00]/5 border border-[#FFCC00]/20 rounded-md px-3 py-2" data-testid="pool-source-hint">
            <span className="font-bold uppercase tracking-wider">Linked</span>
            <span className="text-[#F8F8F8]">
              Pool sized from <strong className="text-[#FFCC00]">{poolSourceClass.name}</strong>
              {poolSourceCount !== null && <> ({poolSourceCount} paid {poolSourceCount === 1 ? 'entry' : 'entries'})</>}
              , not this class. Change in Class Settings.
            </span>
          </div>
        )}

        {hasDivisions ? (
          <div className="space-y-4 mb-4">
            <div>
              <label className="block text-xs text-[#9E9E9E] mb-1">Places Paid (all divisions)</label>
              <select value={placesPaid} onChange={(e) => onSetPlacesPaid(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
                data-testid="places-paid-select">
                {[1,2,3,4,5,6,7,8,9,10,11,12,13,14,15].filter(n => n <= classEntries.length || n <= 15).map(n => <option key={n} value={n}>{n} {n === 1 ? 'place' : 'places'}</option>)}
              </select>
              <p className="text-[10px] text-[#555] mt-1">Same number of places paid in every division. If a division has fewer riders, the pool splits equally among them.</p>
            </div>
            {/* v243 — One-click presets. Producer picks a template and we
                populate pool % and places in one shot. Time splits are set
                separately in Class Settings (v244). */}
            {(PAYOUT_PRESETS[selectedClass.division_type] || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-[#FFCC00] uppercase tracking-wider mb-2">Preset Templates</p>
                <select
                  onChange={(e) => {
                    const pid = e.target.value;
                    if (!pid) return;
                    const preset = (PAYOUT_PRESETS[selectedClass.division_type] || []).find(p => p.id === pid);
                    if (!preset) return;
                    onSetDivisionPoolPct({ ...preset.poolPct });
                    onSetDivisionPlaces({ ...preset.places });
                    e.target.value = '';
                  }}
                  defaultValue=""
                  data-testid="payout-preset-select"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#FFCC00]/40 rounded-md text-white text-sm focus:outline-none focus:border-[#FFCC00]"
                >
                  <option value="">Apply a preset…</option>
                  {(PAYOUT_PRESETS[selectedClass.division_type] || []).map(p => (
                    <option key={p.id} value={p.id}>{p.label} — {p.description}</option>
                  ))}
                </select>
                <p className="text-[10px] text-[#555] mt-1">Applies to the fields below. Time splits are set on the class itself in Show Edit → Class Settings.</p>
              </div>
            )}
            <div>
              <p className="text-xs font-semibold text-[#0096FF] uppercase tracking-wider mb-2">Pool % Per Division</p>
              <div className="space-y-2">
                {divNames.map((divName) => (
                  <div key={divName} className="flex items-center gap-2">
                    <span className="text-white text-sm font-bold w-8">{divName}</span>
                    <div className="flex-1 relative">
                      <input type="number" min="0" max="100" value={divisionPoolPct[divName] || 0}
                        onChange={(e) => onSetDivisionPoolPct(prev => ({...prev, [divName]: parseFloat(e.target.value) || 0}))}
                        className="w-full px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm text-right pr-7 focus:outline-none focus:border-[#0096FF]"
                        data-testid={`div-pct-${divName.toLowerCase()}`} />
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#555] text-xs">%</span>
                    </div>
                  </div>
                ))}
              </div>
              {(() => {
                const total = divNames.reduce((s, n) => s + (divisionPoolPct[n] || 0), 0);
                const isValid = Math.abs(total - 100) < 1;
                return (
                  <p className={`text-xs mt-1 ${isValid ? 'text-[#34C759]' : 'text-[#FF3B30]'}`} data-testid="pct-total">
                    Total: {total}% {isValid ? '' : '(must equal 100%)'}
                  </p>
                );
              })()}
            </div>
            {/* v244 — Save the currently-tweaked pool% + places as this
                class's defaults so producers don't have to re-enter them
                every time they open the Calculator. Time splits live in
                Class Settings (Show Edit), not here. */}
            {onSavePayoutConfig && (
              <button
                onClick={onSavePayoutConfig}
                data-testid="save-payout-config-btn"
                className="w-full px-3 py-2 bg-[#FFCC00]/10 border border-[#FFCC00]/40 hover:bg-[#FFCC00]/20 text-[#FFCC00] text-xs font-bold rounded-md transition-colors"
                title="Save these splits + places to this class so they'll be the defaults every time"
              >
                Save as Class Defaults
              </button>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <label className="block text-xs text-[#9E9E9E] mb-1">Places Paid (Overall)</label>
            <select value={placesPaid} onChange={(e) => onSetPlacesPaid(parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-md text-white text-sm focus:outline-none focus:border-[#0096FF]"
              data-testid="places-paid-select">
              {[1,2,3,4,5,6,7,8,9,10].filter(n => n <= classEntries.length || n <= 10).map(n => <option key={n} value={n}>{n} {n === 1 ? 'place' : 'places'}</option>)}
            </select>
          </div>
        )}

        {/* v260 — Manual override for total pool. Producers use this for
            finals classes where the pool is funded by Go 1 / Go 2 entry
            fees, not the finals roster. Leaving it blank uses the
            entries × fee × payback + added_money formula. */}
        {(() => {
          const estPool = (classEntries.length * effectiveFee * (selectedClass.payback_percent || 70) / 100 + (selectedClass.added_money || 0));
          const overrideNum = poolOverride === '' ? null : Number(poolOverride);
          const isOn = overrideNum !== null && !Number.isNaN(overrideNum) && overrideNum >= 0;
          return (
            <div className="mb-4 p-3 bg-[#0A0A0A] border border-[#262626] rounded-md" data-testid="pool-override-block">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold text-[#FFCC00] uppercase tracking-wider">Override Total Pool</label>
                {isOn && (
                  <button
                    type="button"
                    onClick={() => onSetPoolOverride('')}
                    data-testid="pool-override-clear"
                    className="text-[10px] text-[#9E9E9E] hover:text-white underline"
                  >Clear</button>
                )}
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555] text-sm">$</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="decimal"
                  placeholder={`Auto: $${estPool.toFixed(0)}`}
                  value={poolOverride}
                  onChange={(e) => onSetPoolOverride(e.target.value)}
                  data-testid="pool-override-input"
                  className={`w-full pl-6 pr-3 py-2 bg-[#0A0A0A] border rounded-md text-white text-sm focus:outline-none ${isOn ? 'border-[#FFCC00] focus:border-[#FFCC00]' : 'border-[#262626] focus:border-[#0096FF]'}`}
                />
              </div>
              <p className="text-[10px] text-[#555] mt-1.5 leading-snug">
                Use for finals classes where the pool is funded by Go 1/Go 2 entries, or any class where the auto-computed pool doesn't match reality. Leave blank to auto-compute.
              </p>
            </div>
          );
        })()}

        <button onClick={onCalculatePayouts} disabled={classEntries.length === 0 || payoutLoading}
          className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 bg-[#34C759] text-white rounded-md text-sm font-semibold hover:bg-[#34C759]/80 transition-colors disabled:opacity-40"
          data-testid="calculate-payouts-btn">
          <CurrencyDollar size={16} weight="bold" /> {payoutLoading ? 'Saving...' : 'Calculate & Save Payouts'}
        </button>

        {/* v268 — Projected Payout: print current payouts while the class
            is still LIVE. Uses current completed times (excluding 900+ NTs)
            and the producer-selected places-paid count. Doesn't persist. */}
        <button
          type="button"
          onClick={() => {
            const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
            const params = new URLSearchParams({
              overall_places: String(placesPaid || 1),
              inline: '1',
            });
            if (poolOverride !== '' && poolOverride !== null && !Number.isNaN(Number(poolOverride))) {
              params.set('total_pool_override', String(Number(poolOverride)));
            }
            const url = `${BACKEND}/api/payouts/${selectedClass.id}/projected.pdf?${params.toString()}`;
            window.open(url, '_blank', 'noopener');
          }}
          disabled={classEntries.length === 0}
          className="mt-2 w-full flex items-center justify-center gap-1.5 px-4 py-2 bg-[#0A0A0A] border border-[#FFCC00]/40 text-[#FFCC00] rounded-md text-xs font-semibold hover:bg-[#FFCC00]/10 transition-colors disabled:opacity-40"
          data-testid="projected-payout-btn"
          title="Print current payouts while the class is still live"
        >
          <Printer size={14} weight="bold" /> Print Projected Payout
        </button>
      </div>

      {payoutData && <PayoutResults payoutData={payoutData} show={show} selectedClass={selectedClass} />}

      {!payoutData && classEntries.length === 0 && (
        <div className="text-center py-8">
          <CurrencyDollar size={40} className="text-[#262626] mx-auto mb-3" />
          <p className="text-[#9E9E9E] text-sm">Add entries first to calculate payouts</p>
        </div>
      )}
    </div>
  );
};

const PayoutResults = ({ payoutData, show, selectedClass }) => {
  const PlaceRow = ({ p }) => (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${
      p.projected ? 'bg-[#0A0A0A] border border-dashed border-[#FF9500]/40' :
      p.place === 1 ? 'bg-[#FFD700]/10 border border-[#FFD700]/30' :
      p.place === 2 ? 'bg-[#C0C0C0]/10 border border-[#C0C0C0]/30' :
      p.place === 3 ? 'bg-[#CD7F32]/10 border border-[#CD7F32]/30' :
      'bg-[#0A0A0A] border border-[#262626]'
    }`} data-testid={`payout-place-${p.place}${p.projected ? '-projected' : ''}`}>
      <div className="flex items-center gap-2">
        <span className={`text-base font-black ${
          p.projected ? 'text-[#FF9500]' :
          p.place === 1 ? 'text-[#FFD700]' : p.place === 2 ? 'text-[#C0C0C0]' : p.place === 3 ? 'text-[#CD7F32]' : 'text-[#9E9E9E]'
        }`}>{p.tied ? `T-${p.place}` : p.place}</span>
        <span className="text-[#9E9E9E] text-xs">{p.percentage}%</span>
        {p.projected && <span className="text-[#FF9500] text-[10px] font-bold uppercase tracking-wider">projected</span>}
        {p.tied && <span className="text-[#FF9500] text-[10px] font-bold">SPLIT {p.split_count}</span>}
      </div>
      <span className={`font-bold text-sm ${p.projected ? 'text-[#FF9500]' : 'text-white'}`} style={{fontFamily: 'JetBrains Mono'}}>${p.amount.toFixed(0)}{p.tied ? ' ea' : ''}</span>
    </div>
  );

  // Printed posted-payout: computes each pool's straight split assuming no
  // ties and every place filled. This is the sheet the producer hangs at the
  // announcer's booth; the LIVE results view still uses the actual tie-aware
  // `payoutData.payouts` values that account for real-world splits.
  const handlePrintPayout = () => {
    const hasDivs = payoutData.division_payouts && Object.keys(payoutData.division_payouts).length > 0;
    const escHtml = (s) => String(s || '').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const renderSection = (title, subLabel, placesPaid, pool) => {
      const rows = buildTheoreticalPayout(placesPaid, pool);
      const rowsHtml = rows.map((r) => `
        <tr${r.place % 2 === 0 ? ' style="background:#F8FAFC"' : ''}>
          <td style="text-align:center;font-weight:700;width:60px">${r.placeLabel}</td>
          <td style="text-align:center;color:#666;width:80px">${r.percentage.toFixed(2)}%</td>
          <td style="text-align:right;font-weight:700;font-family:'JetBrains Mono',monospace">$${r.amount}</td>
        </tr>`).join('');
      return `
        <section style="margin-bottom:24px;break-inside:avoid">
          <div style="display:flex;justify-content:space-between;align-items:baseline;border-bottom:2px solid #0A0A0A;padding-bottom:4px;margin-bottom:6px">
            <h2 style="margin:0;font-size:15px;font-weight:800">${escHtml(title)}</h2>
            <span style="font-size:11px;color:#666">${escHtml(subLabel)}</span>
          </div>
          <div style="font-size:11px;color:#888;margin-bottom:6px">
            <strong style="color:#0A0A0A">${placesPaid} place${placesPaid === 1 ? '' : 's'} paid</strong>
            &nbsp;·&nbsp; Pool: <strong style="color:#1E8E3E">$${Number(pool).toFixed(0)}</strong>
            <em style="color:#999;margin-left:8px">(no ties, all places filled)</em>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:12px">
            <thead><tr style="background:#0A0A0A;color:#fff">
              <th style="text-align:center;padding:6px 8px;width:60px">Place</th>
              <th style="text-align:center;padding:6px 8px;width:80px">%</th>
              <th style="text-align:right;padding:6px 8px">Payout</th>
            </tr></thead>
            <tbody>${rowsHtml}</tbody>
          </table>
        </section>`;
    };

    let body = '';
    if (hasDivs) {
      body = Object.entries(payoutData.division_payouts)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([divName, divData]) => {
          // For divisions with no qualifying riders yet (typical pre-show
          // print run), fall back to the CONFIGURED pool + places so the
          // office workers can fill envelopes / write checks based on the
          // projected payouts.
          const isEmpty = divData.is_empty || divData.entries === 0;
          const sectionPool = isEmpty ? (divData.pool_original || 0) : divData.pool;
          const sectionPlaces = isEmpty ? (divData.places_configured || 0) : divData.places_paid;
          const subLabel = isEmpty
            ? `${divData.pool_pct || 0}% of pool · projected (no riders yet)`
            : `${divData.entries} entries · ${divData.pool_pct || 0}% of pool`;
          return renderSection(divName, subLabel, sectionPlaces, sectionPool);
        }).join('');
    } else {
      body = renderSection('Overall', `${payoutData.num_entries} entries`, payoutData.overall_places_paid, payoutData.total_pool);
    }

    const when = new Date().toLocaleString();
    const html = `<!doctype html><html><head><title>Posted Payout — ${escHtml(selectedClass?.name)}</title><meta charset="utf-8"/>
      <style>
        *{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important;color-adjust:exact !important}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111;padding:28px;margin:0;max-width:720px}
        h1{margin:0 0 2px;font-size:22px;font-weight:800}
        .sub{color:#555;font-size:13px;margin-bottom:4px}
        .meta{color:#888;font-size:11px;margin-bottom:16px}
        table td{padding:6px 8px;border-bottom:1px solid #eee}
        .foot{margin-top:28px;padding-top:12px;border-top:1px solid #ddd;font-size:10px;color:#999;text-align:center}
        @media print{@page{margin:0.5in}}
      </style></head><body>
      <h1>${escHtml(show?.name)}</h1>
      <div class="sub">${escHtml(selectedClass?.name)} &middot; Posted Payout Sheet</div>
      <div class="meta">Total Pool: <strong style="color:#1E8E3E">$${Number(payoutData.total_pool).toFixed(0)}</strong>
      &nbsp;·&nbsp; ${payoutData.num_entries} entries &nbsp;·&nbsp; Generated ${escHtml(when)}</div>
      ${body}
      <div class="foot">Posted payout assumes no ties and every place filled. Actual payouts may adjust for ties and unfilled places — see live results.</div>
      <script>window.onload=()=>{window.print()};</script>
    </body></html>`;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };

  return (
    <div className="mt-4 space-y-4" data-testid="payout-results">
      <div className="flex justify-between items-center pb-2 border-b border-[#262626]">
        <div className="flex gap-3 items-center text-xs text-[#9E9E9E] flex-wrap">
          <span>{payoutData.num_entries} entries</span>
          <span>Pool: <span className="text-[#34C759] font-semibold">${payoutData.total_pool}</span></span>
          {payoutData.pool_overridden && (
            <span
              className="text-[9px] font-bold text-[#FFCC00] bg-[#FFCC00]/15 border border-[#FFCC00]/40 rounded px-1.5 py-0.5 uppercase tracking-wider"
              data-testid="pool-override-badge"
              title={typeof payoutData.computed_pool === 'number' ? `Auto-computed pool would have been $${payoutData.computed_pool}` : 'Producer set the total pool manually'}
            >Manual override</span>
          )}
        </div>
        <button
          onClick={handlePrintPayout}
          className="flex items-center gap-1 px-2.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-white hover:bg-[#262626] transition-colors"
          data-testid="print-payout-btn"
          title="Open a printable posted-payout sheet (no ties, all places filled)"
        >
          <Printer size={12} weight="bold" /> Print Payout
        </button>
      </div>

      {payoutData.division_payouts && Object.keys(payoutData.division_payouts).length > 0 ? (
        Object.entries(payoutData.division_payouts).sort(([a],[b]) => a.localeCompare(b)).map(([divName, divData]) => {
          const isEmpty = divData.is_empty || divData.entries === 0;
          const redistIn = Number(divData.redistributed_in || 0);
          const redistOut = Number(divData.redistributed_out || 0);
          const origPool = Number(divData.pool_original || divData.pool || 0);
          return (
            <div
              key={divName}
              data-testid={`division-payout-${divName.toLowerCase()}`}
              className={isEmpty ? 'opacity-75' : ''}
            >
              <div className="flex items-center justify-between mb-2 flex-wrap gap-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-[#0096FF]">{divName}</span>
                  {isEmpty && (
                    <span
                      className="text-[9px] font-bold text-[#FF9500] bg-[#FF9500]/15 border border-[#FF9500]/40 rounded px-1.5 py-0.5 uppercase tracking-wider"
                      data-testid={`division-empty-badge-${divName.toLowerCase()}`}
                      title="No qualifying riders — pool redistributed across remaining paying places"
                    >
                      Empty · Redistributed
                    </span>
                  )}
                  {!isEmpty && redistIn > 0 && (
                    <span
                      className="text-[9px] font-bold text-[#34C759] bg-[#34C759]/15 border border-[#34C759]/40 rounded px-1.5 py-0.5 uppercase tracking-wider"
                      data-testid={`division-bonus-badge-${divName.toLowerCase()}`}
                      title="This division received money from an empty division"
                    >
                      +${redistIn} bonus
                    </span>
                  )}
                </div>
                <span className="text-xs text-[#9E9E9E]">
                  {divData.entries} entries &bull; {divData.pool_pct || 0}% &bull;{' '}
                  {isEmpty ? (
                    <span title={`Configured pool of $${origPool} was split across the remaining paying places.`}>
                      <span className="line-through text-[#666]">${origPool}</span>{' '}
                      <span className="text-[#FF9500]">→ $0</span>
                    </span>
                  ) : redistIn > 0 ? (
                    <span title={`Original division pool $${origPool} + $${redistIn} redistributed in from empty division(s)`}>
                      ${divData.pool}{' '}
                      <span className="text-[#34C759]">(was ${origPool})</span>
                    </span>
                  ) : (
                    <>${divData.pool}</>
                  )}
                  {' '}&bull;{' '}
                  <span className="text-white font-semibold">
                    {divData.places_paid} place{divData.places_paid === 1 ? '' : 's'} paid
                  </span>
                </span>
              </div>
              {isEmpty ? (
                <div className="space-y-1.5" data-testid={`division-projected-${divName.toLowerCase()}`}>
                  <p className="text-[10px] text-[#FF9500] italic px-1 pb-1" data-testid={`division-empty-note-${divName.toLowerCase()}`}>
                    No qualifying riders yet — payouts shown are projections.
                    {redistOut > 0 && ' Configured pool was redistributed to other paying places.'}
                  </p>
                  {(divData.projected_payouts || []).map((p) => (
                    <PlaceRow key={p.place} p={{...p, projected: true}} />
                  ))}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {divData.payouts.map((p) => <PlaceRow key={p.place} p={p} />)}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-sm font-bold text-white">Overall</p>
            <span className="text-xs text-[#9E9E9E]">{payoutData.overall_places_paid} place{payoutData.overall_places_paid === 1 ? '' : 's'} paid</span>
          </div>
          <div className="space-y-1.5">
            {payoutData.payouts.map((p) => <PlaceRow key={p.place} p={p} />)}
          </div>
        </div>
      )}

      {/* Saved confirmation */}
      {payoutData && (
        <div className="mt-4 pt-3 border-t border-[#262626]">
          <p className="text-[#34C759] text-xs font-semibold text-center">Payouts saved — visible on live results</p>
        </div>
      )}
    </div>
  );
};
